import { Box, type SxProps, type Theme } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { getBizmedAiMarkdownComponents, type BizmedAiMarkdownVariant } from "./bizmedAiMarkdownComponents";
import { normalizeStreamingAiMarkdown } from "./normalizeStreamingAiMarkdown";

export type BizmedAiMarkdownProps = {
  markdown: string;
  /** @default true */
  normalize?: boolean;
  /** @default clinical */
  variant?: BizmedAiMarkdownVariant;
  className?: string;
  sx?: SxProps<Theme>;
};

const clinicalWrapperSx: SxProps<Theme> = {
  "& > h3:first-of-type, & > h4:first-of-type": { mt: 0 },
  "& h3 + p, & h4 + p": { mt: 0.5 },
  "& hr": { my: 2.5 },
};

const chatbubbleWrapperSx: SxProps<Theme> = {
  "& > h3:first-of-type, & > h4:first-of-type": { mt: 0 },
  "& h3 + p, & h4 + p": { mt: 0.25 },
  "& hr": { my: 1.25 },
};

export function BizmedAiMarkdown({
  markdown,
  normalize = true,
  variant = "clinical",
  className,
  sx,
}: Readonly<BizmedAiMarkdownProps>) {
  const source = normalize ? normalizeStreamingAiMarkdown(markdown) : markdown;
  const trimmed = source.trim();

  if (!trimmed) {
    return null;
  }

  const components = getBizmedAiMarkdownComponents(variant);
  const baseSx = variant === "clinical" ? clinicalWrapperSx : chatbubbleWrapperSx;

  return (
    <Box className={className} sx={[baseSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={components}>
        {trimmed}
      </ReactMarkdown>
    </Box>
  );
}
